import { NgClass } from '@angular/common';
import {
  Component,
  computed,
  HostBinding,
  input,
  OnInit,
  viewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  FormGroupWrapper,
  FormWrapperBuilder,
} from '@maximizer/core/shared/ui/forms';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import {
  LeadService,
  NavigationService,
  OutlookStore,
  TrackerService,
} from '@maximizer/outlook/shared/data-access';
import {
  EventAction,
  EventJourney,
  EventMetadata,
  EventModule,
  OutlookLeadForm,
  OutlookLeadSearch,
} from '@maximizer/outlook/shared/domain';
import {
  OUTLOOK_SHARED_UI,
  OutlookNotificationComponent,
  PHONE_VALIDATORS,
} from '@maximizer/outlook/shared/ui';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { catchError, finalize, forkJoin, of } from 'rxjs';
import { ListItem } from '@maximizer/core/shared/domain';

@Component({
  selector: 'maximizer-edit-lead',
  templateUrl: './edit-lead.component.html',
  imports: [
    FormsModule,
    KENDO_BUTTON,
    KENDO_DROPDOWNS,
    KENDO_INPUTS,
    KENDO_LABEL,
    NgClass,
    OUTLOOK_SHARED_UI,
    ReactiveFormsModule,
    TranslatePipe,
    UI_KENDO,
    UI_LOADER,
  ],
})
export class EditLeadComponent implements OnInit {
  @HostBinding('class.max-outlook') hostClass = true;

  notification =
    viewChild.required<OutlookNotificationComponent>('notification');

  id = input.required<string>();

  leadKey = computed(() => {
    return decodeURI(this.id());
  });

  loading = true;
  addPhone2 = false;
  leadData?: OutlookLeadSearch;
  form?: FormGroupWrapper<OutlookLeadForm>;
  countries: ListItem<string>[] = [];

  constructor(
    private readonly leadService: LeadService,
    private readonly navigationService: NavigationService,
    private readonly tracker: TrackerService,
    public outlookStore: OutlookStore,
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  private loadData(): void {
    forkJoin({
      lead: this.leadService.getByKey(this.leadKey()),
      fieldOptions: this.leadService.getFieldOption(),
    })
      .pipe(
        catchError(async () => {
          this.notification().show('load', 'error');
        }),
        finalize(() => (this.loading = false)),
      )
      .subscribe((data) => {
        if (!data) return;
        if (!data.lead) return;

        if (data.lead.phone2 && data.lead.phone2 != '') this.addPhone2 = true;
        this.countries = data.fieldOptions.addressCountry;

        this.setForm(data.lead);
        this.leadData = data.lead;
      });
  }

  private setForm(lead: OutlookLeadSearch): void {
    this.form = FormWrapperBuilder.group<OutlookLeadForm>({
      key: null,
      firstName: [lead.firstName, [Validators.maxLength(79)]],
      lastName: [
        lead.lastName,
        [
          Validators.nullValidator,
          Validators.maxLength(79),
          Validators.required,
        ],
      ],
      email: [lead.email, []],
      address1: [lead.address?.address1, [Validators.maxLength(79)]],
      address2: [lead.address?.address2, [Validators.maxLength(79)]],
      phone1: [lead.phone1 ?? '', PHONE_VALIDATORS],
      phone2: [lead.phone2 ?? '', PHONE_VALIDATORS],
      company: [lead.companyName ?? '', [Validators.maxLength(79)]],
      position: [lead.position, [Validators.maxLength(79)]],
      website: [lead.website, [Validators.maxLength(255)]],
      city: [lead.address?.city, [Validators.maxLength(79)]],
      province: [lead.address?.provinceState, [Validators.maxLength(79)]],
      postalCode: [lead.address?.zipcode, [Validators.maxLength(79)]],
      country: [null, [Validators.maxLength(79)]],
    });

    if (lead.address?.country) {
      this.form.group.patchValue({ country: lead.address.country });
    }
  }

  save(): void {
    if (!this.form) {
      return;
    }

    const validation = this.form.validate();

    if (!validation.valid) return;
    this.form.patch({
      key: this.leadKey(),
    });

    this.loading = true;

    this.leadService
      .update(this.form.value)
      .pipe(
        catchError(() => {
          this.notification().show('save', 'error');
          return of(false);
        }),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe((result) => {
        const success = !!result;

        this.tracker.trackEvent({
          module: EventModule.Entry,
          journey: EventJourney.Update,
          action: EventAction.Completed,
          metadata: {
            entry: 'lead',
            result: success ? EventMetadata.Success : EventMetadata.Failed,
          },
        });

        if (success) {
          this.notification().show('save', 'success', false);
          this.navigationService.navigateBack();
        }
      });
  }

  onRemovePhone(): void {
    this.form?.patch({ phone2: '' });
    this.addPhone2 = false;
  }

  cancel(): void {
    this.navigationService.navigateBack();
  }
}
